// React
import React, { useState, useEffect } from "react";

// External Components
import { motion, useDragControls } from "framer-motion";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../hooks/Dropable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faSquareXmark,
  faTrash,
  faPlus,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

// Internal  Components
import AutoResizeTextArea from "../../../../components/ui/AutoResizeTextarea/AutosizeTextarea";
import Input from "../TaskInput/Input";

function Callout({
  id,
  taskId,
  steps,
  description,
  notes,
  overlay,
  calloutActive,
  columnId,
  onDragEndSteps,
  addStep,
  aiHelp,
  handleCalloutDisplay,
  handleIndexChange,
  updateDescription,
  updateStep,
  updateNotes,
  toggleStep,
  toggleOverlay,
  deleteStep,
  deleteObjective,
  deleteSubtask,
  promoteStepToMoment,
  momentValues,
  viewType,
}) {
  const [warning, setWarning] = useState(false);
  const [inp, setInp] = useState();
  const [aiLoading, setAiLoading] = useState(false);

  const dragControls = useDragControls();

  const startDrag = (event) => {
    dragControls.start(event);
  };

  const fetchOpenAIResponse = async (prompt) => {
    try {
      const response = await axios.post(
        "https://api.nowasteddays.io/planning",
        {
          message: prompt,
        },
      );

      if (response) {
        return response.data.output[0].content[0].text;
      }
    } catch (error) {
      console.error("Error fetching response:", error);
      return "Error: Unable to fetch response.";
    }
  };

  const handleAiResponse = (res) => {
    let data = JSON.parse(res);
    return { title: data.description, steps: data.steps };
  };

  const handleAiStepsAddition = async (task, steps) => {
    try {
      const newSteps = await fetchOpenAIResponse(task); // Await the promise
      const finalSteps = handleAiResponse(newSteps);

      if (Array.isArray(finalSteps.steps)) {
        updateDescription(finalSteps.title, columnId, taskId);

        // If you are using a state setter like setSteps from useState:
        // setSteps(prevSteps => [...prevSteps, ...newSteps]);

        // If 'steps' is a mutable array passed by reference and you want to modify it directly:
        // (This is generally not recommended for React state, prefer immutable updates)
        for (let i = 0; i < finalSteps.steps.length; i++) {
          steps.push(finalSteps.steps[i]);
        }
        // If 'steps' is React state and 'setSteps' is its updater:
        // setSteps(currentSteps => [...currentSteps, ...newSteps]);
      } else {
        console.error("AI response is not an array:", finalSteps.steps);
      }
    } catch (error) {
      console.error("Error fetching AI steps:", error);
      // Optionally, handle the error in the UI, e.g., set an error state
    }
  };

  return (
    <>
      <div
        className={
          calloutActive ? "calloutContainer--overlay" : "calloutContainer"
        }
      >
        <motion.div
          className={
            calloutActive
              ? `objective__callout--active ${columnId}`
              : "objective__callout"
          }
          style={{ zIndex: 30 }}
        >
          <div className="callout__header">
            <AutoResizeTextArea
              className="callout__header--input"
              type="text"
              value={description}
              autoComplete="off"
              onChange={(event) =>
                updateDescription(event, columnId, taskId)
              }
            ></AutoResizeTextArea>
            <div className="callout__button--div">
              {/* <FontAwesomeIcon
                className="callout--icon"
                data-tooltip="Double check your task is described perfectly, before asking NWD AI to translate it into a series of steps"
                icon={faWandMagicSparkles}
                data-display-name="art"
                onClick={async () => {
                  setAiLoading(true);
                  await handleAiStepsAddition(description, steps);
                  setAiLoading(false);
                }}
              /> */}
              <FontAwesomeIcon
                className="callout--icon"
                icon={faSquareXmark}
                data-display-name="callout"
                onClick={(e) => {
                  handleCalloutDisplay(e);
                  toggleOverlay(false);
                }}
              />
            </div>
          </div>

          <DragDropContext
            onDragEnd={(result) =>
              onDragEndSteps(result, columnId, taskId)
            }
          >
            <StrictModeDroppable
              droppableId={`Steps-${id}`}
              type="STEPS"
            >
              {(provided) => (
                <div
                  className="callout__actions"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {aiLoading ? (
                    <div
                      className="objective__col--wrapper"
                      style={{ width: "100%" }}
                    >
                      <div className={"objective__col--task--callout"}>
                        <div className="task__column--wrapper">
                          <div className="task__column">
                            <div className="task__icon--wrapper">
                              <div className={"task__radio"}></div>
                            </div>
                          </div>
                        </div>
                        <div className="task__container">
                          <AutoResizeTextArea
                            className={"task--input--loading"}
                            value=""
                          />
                        </div>

                        <FontAwesomeIcon
                          className="objective__col--icon trash"
                          icon={faTrash}
                        />
                      </div>
                    </div>
                  ) : (
                    steps &&
                    steps.length > 0 &&
                    steps.map((d, i) => (
                      <Draggable key={d.id} draggableId={d.id} index={i}>
                        {(provided) => (
                          <div
                            className="callout-step__row"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div
                              className="callout-step__grip"
                              {...provided.dragHandleProps}
                            >
                              ⠿
                            </div>
                            <Input
                              task={{ ...d, description: d.action }}
                              taskId={d.id}
                              title={columnId}
                              mode="step"
                              momentValues={momentValues}
                              onPromoteToMoment={(text, momentKey) => {
                                if (promoteStepToMoment) {
                                  promoteStepToMoment(momentKey, text);
                                }
                              }}
                              deleteSubtask={() =>
                                deleteStep(columnId, taskId, d.id)
                              }
                              toggleSubTask={() =>
                                toggleStep(taskId, d.id, columnId)
                              }
                              toggleOverlay={() => {}}
                              updateDescription={(event) =>
                                updateStep(columnId, taskId, d.id, event)
                              }
                              handleCalloutDisplay={() => {}}
                              handleUrgencyChange={() => {}}
                              handleEstimateChange={() => {}}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}

                  {provided.placeholder}
                  <div className="objective__col--name">
                    <FontAwesomeIcon
                      className="objective__col--icon"
                      icon={faPlus}
                      onClick={async () => {
                        await addStep(inp, columnId, taskId);
                        setInp("");
                      }}
                    />
                    <input
                      type="text"
                      className="col__name--input"
                      placeholder="Add a new step"
                      autoComplete="off"
                      name={`${columnId}-${taskId}`}
                      onChange={(e) => setInp(e.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.repeat) {
                          event.preventDefault();
                          addStep(event, columnId, taskId);
                        }
                      }}
                    />
                    <FontAwesomeIcon
                      className="objective__col--icon ellipsis"
                      icon={faEllipsisVertical}
                    />
                  </div>
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
          <div className="callout__notes">
            <textarea
              className="notes"
              placeholder="Add Notes"
              value={notes}
              onChange={(event) => updateNotes(columnId, taskId, event)}
            />

            <div
              className="callout__button"
              data-display-name="callout"
              onClick={(e) => {
                deleteSubtask(columnId, taskId);
                handleCalloutDisplay(e);
              }}
            >
              Delete Task
            </div>
          </div>
        </motion.div>
        <div
          className={warning ? "warning__callout--active" : "warning__callout"}
        >
          <strong>Are you sure you want to delete this objective?</strong>
          <div className="warning__button--row">
            <div
              className="callout__button"
              onClick={() => {
                deleteObjective(columnId);
                setWarning(false);
              }}
            >
              Yes
            </div>
            <div className="callout__button" onClick={() => setWarning(false)}>
              No
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Callout;
